import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { auditLogApi, AuditEntry, AuditQuery } from '@/lib/auditLog';

/**
 * Best-effort deep-link from an audit row to the object it touched. The audit
 * log is company-wide, so we route via the entry's own workspace_id. Unknown
 * kinds return null and render as plain text.
 */
/** Download the currently-loaded audit rows as a CSV. Client-side only —
 * exports exactly what the filter shows. */
function exportAuditCsv(entries: AuditEntry[]): void {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const header = ['created_at', 'user_id', 'action', 'target_kind', 'target_id', 'workspace_id', 'ip'];
  const lines = [header.join(',')];
  for (const e of entries) {
    lines.push([e.created_at, e.user_id, e.action, e.target_kind, e.target_id, e.workspace_id, e.ip].map(esc).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cadencez-audit-log.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function auditTargetHref(e: AuditEntry): string | null {
  if (!e.workspace_id || !e.target_kind) return null;
  const ws = `/app/workspace/${e.workspace_id}`;
  switch (e.target_kind) {
    case 'forecast': return `${ws}/forecast`;
    case 'workbook': return e.target_id ? `${ws}/workbook/${e.target_id}` : `${ws}/workbooks`;
    case 'entity': return `${ws}/entities`;
    case 'fx_rate': return `${ws}/fx-rates`;
    case 'variance': return ws;
    case 'board_pack': return `${ws}/board-pack`;
    case 'approval': return `${ws}/approvals`;
    case 'data_source': return `${ws}/connectors`;
    default: return null;
  }
}
import { api } from '@/lib/api';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { AppShell } from '@/components/app/AppShell';

type Range = '24h' | '7d' | '30d' | 'all';

function rangeToSince(r: Range): string | undefined {
  if (r === 'all') return undefined;
  const now = Date.now();
  const ms = r === '24h' ? 24 * 60 * 60 * 1000 : r === '7d' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;
  return new Date(now - ms).toISOString();
}

export function AuditLogPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  // Scope to the CURRENT workspace's company (not blindly companies[0], which
  // breaks for users in more than one company).
  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<{ companyId: string }>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });
  const companyId = workspace?.companyId;

  const [range, setRange] = useState<Range>('7d');
  const [actor, setActor] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [pages, setPages] = useState<number>(1);

  const query: AuditQuery = useMemo(() => ({
    limit: 100,
    offset: 0,
    since: rangeToSince(range),
    actor: actor.trim() || undefined,
    q: q.trim() || undefined,
  }), [range, actor, q]);

  // The base query is one page. To show "load more" we maintain a manual
  // pages count that re-issues with offset = (pages - 1) * 100.
  const queries = useQuery<AuditEntry[]>({
    queryKey: ['audit-log', companyId, JSON.stringify(query), pages],
    queryFn: async () => {
      const all: AuditEntry[] = [];
      for (let i = 0; i < pages; i++) {
        const page = await auditLogApi.list(companyId!, { ...query, offset: i * (query.limit ?? 100) });
        all.push(...page);
        if (page.length < (query.limit ?? 100)) break;
      }
      return all;
    },
    enabled: !!companyId,
    retry: false,
  });

  const entries = queries.data ?? [];
  const reachedEnd = entries.length < pages * (query.limit ?? 100);

  return (
    <AppShell title={t('audit_log.title')} subtitle={t('audit_log.subtitle')}>
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('audit_log.title')}</h1>
          <p className="mt-1 text-ink-600">{t('audit_log.subtitle')}</p>
        </div>

        {/* Filter strip */}
        <div className="rounded-md border border-border bg-white p-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border border-border p-0.5 bg-ink-50">
            {(['24h', '7d', '30d', 'all'] as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => { setRange(r); setPages(1); }}
                className={
                  'px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ' +
                  (r === range ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')
                }
              >
                {t(`audit_log.range_${r}`)}
              </button>
            ))}
          </div>
          <input
            value={actor}
            onChange={(e) => { setActor(e.target.value); setPages(1); }}
            placeholder={t('audit_log.filter_actor')}
            className="num rounded-lg border border-border bg-white px-3 py-2 text-xs w-64"
          />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPages(1); }}
            placeholder={t('audit_log.filter_search')}
            className="flex-1 min-w-[200px] rounded-lg border border-border bg-white px-3 py-2 text-sm"
          />
          <button
            onClick={() => exportAuditCsv(entries)}
            disabled={entries.length === 0}
            className="px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-foreground/[0.04] disabled:opacity-50"
          >
            {t('audit_log.export_csv', { defaultValue: 'Export CSV' })}
          </button>
        </div>

        {queries.error && (
          isPlanGateError(queries.error) ? (
            <UpgradeWall wsId={wsId!} requiredTier="enterprise" featureName={t('audit_log.feature_name', { defaultValue: 'The audit log' })} />
          ) : (
            <div className="rounded-lg bg-warn-500/10 border border-warn-500/30 text-warn-600 px-3 py-2 text-sm">
              {(queries.error as Error).message}
            </div>
          )
        )}

        {queries.isLoading && pages === 1 && <Loading t={t} />}

        {!queries.isLoading && entries.length === 0 && (
          <div className="rounded-md border-2 border-dashed border-border bg-white p-12 text-center text-muted-foreground">
            {t('audit_log.empty')}
          </div>
        )}

        {entries.length > 0 && (
          <>
            <div className="rounded-md border border-border bg-white overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-ink-100">
                    <th className="py-2 pl-4 pr-3 font-medium">{t('audit_log.th_when')}</th>
                    <th className="py-2 px-3 font-medium">{t('audit_log.th_actor')}</th>
                    <th className="py-2 px-3 font-medium">{t('audit_log.th_action')}</th>
                    <th className="py-2 px-3 font-medium">{t('audit_log.th_target')}</th>
                    <th className="py-2 px-3 font-medium">{t('audit_log.th_payload')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <EntryRow key={e.id} entry={e} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-center">
              {reachedEnd ? (
                <span className="text-xs text-muted-foreground">{t('audit_log.no_more')}</span>
              ) : (
                <button
                  onClick={() => setPages((p) => p + 1)}
                  disabled={queries.isFetching}
                  className="px-4 py-2 rounded-lg border border-border text-ink-700 hover:bg-foreground/[0.04] text-xs font-semibold disabled:opacity-60"
                >
                  {queries.isFetching ? t('common.loading') : t('audit_log.load_more')}
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </AppShell>
  );
}

function EntryRow({ entry: e }: { entry: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const payloadStr = e.payload ? JSON.stringify(e.payload) : '';
  return (
    <tr className="border-b border-ink-50 align-top">
      <td className="py-2 pl-4 pr-3 num text-ink-700 text-xs whitespace-nowrap">
        {new Date(e.created_at).toLocaleString()}
      </td>
      <td className="py-2 px-3 num text-xs text-ink-600 truncate max-w-[14ch]">
        {e.user_id ? e.user_id.slice(0, 8) : '—'}
      </td>
      <td className="py-2 px-3 num text-foreground font-medium">{e.action}</td>
      <td className="py-2 px-3 num text-ink-700 text-xs">
        {(() => {
          const href = auditTargetHref(e);
          const idPart = e.target_id ? ` · ${String(e.target_id).slice(0, 12)}` : '';
          if (!href) return <>{e.target_kind ?? ''}<span className="text-ink-400">{idPart}</span></>;
          return (
            <Link to={href} className="hover:text-emerald-700 hover:underline">
              {e.target_kind}<span className="text-ink-400">{idPart}</span>
            </Link>
          );
        })()}
      </td>
      <td className="py-2 px-3 text-xs text-ink-600">
        {!payloadStr ? (
          <span className="text-ink-400">—</span>
        ) : expanded ? (
          <pre className="whitespace-pre-wrap font-mono text-[11px] max-w-[60ch]">
            {JSON.stringify(e.payload, null, 2)}
          </pre>
        ) : (
          <button
            onClick={() => setExpanded(true)}
            className="text-left max-w-[40ch] truncate hover:text-foreground num"
            title={payloadStr}
          >
            {payloadStr}
          </button>
        )}
      </td>
    </tr>
  );
}

function Loading({ t }: { t: (k: string) => string }) {
  return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">{t('common.loading')}</div>;
}
